import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/api/client";
import { AuthLayout, Field } from "@/components/auth/AuthLayout";

export default function Login() {
    const { t } = useTranslation();
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const location = useLocation() as { state?: { from?: { pathname: string } } };

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showPw, setShowPw] = useState(false);

    const submitPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr(null); setLoading(true);
        try {
            await api.post("/auth/login", { email: email.trim(), password });
            // MFA disabled platform-wide (Phase M5.x). Login response is always
            // a full session; go straight to the app.
            await refresh();
            const to = location.state?.from?.pathname || "/";
            navigate(to, { replace: true });
        } catch (e: any) {
            const raw = formatApiError(e);
            const map: Record<string, string> = {
                signup_pending: t("auth.err.signup_pending"),
                signup_rejected: t("auth.err.signup_rejected"),
                signup_awaiting_activation: t("auth.err.signup_awaiting_activation"),
                service_unavailable_db: t("auth.err.service_unavailable_db"),
                server_error_generic: t("auth.err.server_error_generic"),
            };
            setErr(map[raw] || raw);
        } finally { setLoading(false); }
    };

    return (
        <AuthLayout title={t("auth.login.title")}>
            <form onSubmit={submitPassword} className="space-y-3.5" data-testid="login-form">
                <Field label={t("auth.email")}>
                    <input data-testid="login-email-input" type="email" required autoComplete="email" placeholder={t("auth.email")}
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }} />
                </Field>
                <Field label={t("auth.password")}>
                    <div className="relative">
                        <input data-testid="login-password-input" type={showPw ? "text" : "password"} required autoComplete="current-password" placeholder={t("auth.password")}
                            value={password} onChange={(e) => setPassword(e.target.value)}
                            className="surface-input pl-3 pr-10 py-2 w-full text-sm" style={{ minHeight: 40 }} />
                        <button type="button" data-testid="login-password-toggle" onClick={() => setShowPw((v) => !v)}
                            aria-label={showPw ? t("auth.password.hide") : t("auth.password.show")}
                            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-[var(--border)]" style={{ color: "var(--text-muted)" }}>
                            {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                    </div>
                </Field>
                {err && (
                    <div className="flex items-start gap-2 rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="login-error">
                        <AlertCircle size={16} className="mt-0.5" />
                        <div className="flex-1 space-y-2">
                            <span>{err}</span>
                            {(err === t("auth.err.service_unavailable_db") || err === t("auth.err.server_error_generic")) && (
                                <button type="button" data-testid="login-retry" onClick={() => submitPassword(new Event("submit") as any)}
                                    className="text-xs underline" style={{ color: "var(--error-fg)" }}>{t("auth.err.retry")}</button>
                            )}
                        </div>
                    </div>
                )}
                <button data-testid="login-submit" type="submit" disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60"
                    style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {t("auth.login.submit")}
                </button>
                <div className="flex justify-between text-xs">
                    <Link data-testid="forgot-link" to="/forgot-password" style={{ color: "var(--accent)" }} className="hover:underline">{t("auth.forgot")}</Link>
                    <Link data-testid="register-link" to="/register" style={{ color: "var(--accent)" }} className="hover:underline">{t("auth.footer.signup")}</Link>
                </div>
            </form>
        </AuthLayout>
    );
}
